import { ClientColumns, SORT_FIELD_MAP } from "./ClientColumns";
import React, { useCallback, useState, useEffect } from "react";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { Table, Pagination, Input, Select, Button } from "antd";
import { SearchOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Endpoints from "../../api/endpoints.js";
import routes from "../../utils/routes.js";
import useSuccessErrorModal from "../../hooks/successErrorModal/useSuccessErrorModal";
import EndpointFunctions from "../../utils/Functions/endpointFunctions.js";
import { debounce } from "lodash";

const DEFAULT_SORT_FIELD = "id";
const DEFAULT_SORT_DIRECTION = "desc";

const Clients = () => {
  const { showModal, closeModal, ModalComponent } = useSuccessErrorModal();

  const [filter, setFilter] = useState({
    total: 0,
    page: 0,
    size: 10,
    pageCount: 0,
    search: "",
    isPermanent: null,
    channel: null,
    hasWallet: null,
    sortField: DEFAULT_SORT_FIELD,
    sortDirection: DEFAULT_SORT_DIRECTION,
  });

  const [tableData, setTableData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();

  useEffect(() => {
    const controller = new AbortController();
    fetchClientData(controller);
    return () => controller.abort();
  }, [
    filter.page,
    filter.size,
    filter.search,
    filter.isPermanent,
    filter.channel,
    filter.hasWallet,
    filter.sortField,
    filter.sortDirection,
  ]);

  const fetchClientData = async (controller) => {
    setTableLoading(true);

    const result = await EndpointFunctions.fetchData({
      endpointURL: Endpoints.searchClient,
      axiosPrivate: axiosPrivate,
      httpParams: {
        page: filter.page,
        size: filter.size,
        searchString: filter.search,
        permanents: filter.isPermanent,
        channel: filter.channel,
        hasWallet: filter.hasWallet,
        sortField: filter.sortField,
        sortDirection: filter.sortDirection,
      },
      controller: controller,
    });

    // If the effect was cleaned up before the response arrived, drop it.
    // Prevents a stale response from overwriting a fresher one.
    if (controller.signal.aborted) return;

    if (result.success) {
      setTableData(result?.data.data);
      setFilter((prev) => ({
        ...prev,
        total: result?.data.total,
        size: result?.data.pageSize,
        pageCount: result?.data.pageCount,
      }));
    } else {
      showModal(result.errorTitle, result.errorMessage, () => {
        closeModal();
      });
    }
    setTableLoading(false);
  };

  const paginationChange = (page, pageSize) => {
    setFilter((prev) => ({ ...prev, page: page - 1, size: pageSize }));
  };

  const handleSearch = (searchValue) => {
    debouncedSearch(searchValue);
  };

  const debouncedSearch = useCallback(
    debounce((searchValue) => {
      setFilter((prev) => ({ ...prev, search: searchValue, page: 0 }));
    }, 500),
    []
  );

  const handlePermanentChange = (value) => {
    setFilter((prev) => ({ ...prev, isPermanent: value ?? null, page: 0 }));
  };

  const handleChannelChange = (value) => {
    setFilter((prev) => ({ ...prev, channel: value ?? null, page: 0 }));
  };

  const handleWalletChange = (value) => {
    setFilter((prev) => ({ ...prev, hasWallet: value ?? null, page: 0 }));
  };

  const handleTableChange = (_pagination, _filters, sorter) => {
    const field = sorter?.field;
    const order = sorter?.order;

    if (!order || !field) {
      setFilter((prev) => ({
        ...prev,
        sortField: DEFAULT_SORT_FIELD,
        sortDirection: DEFAULT_SORT_DIRECTION,
        page: 0,
      }));
      return;
    }

    const backendField = SORT_FIELD_MAP[field] ?? DEFAULT_SORT_FIELD;
    const backendDirection = order === "ascend" ? "asc" : "desc";
    setFilter((prev) => ({
      ...prev,
      sortField: backendField,
      sortDirection: backendDirection,
      page: 0,
    }));
  };

  const goToClientDetails = (clientId) => {
    navigate(`${routes.clientDetails}/${clientId}`);
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <Input.Search
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Arama yapınız..."
          style={{ width: 400 }}
          size="large"
          loading={tableLoading}
          prefix={<SearchOutlined />}
          allowClear
        />
        <Select
          style={{ width: 280 }}
          size="large"
          placeholder="Kullanıcı Durumu"
          allowClear
          options={[
            { label: "Telefon Numarası Ekli", value: true },
            { label: "Telefon Numarası Ekli Olmayan", value: false },
          ]}
          onChange={handlePermanentChange}
        />
        <Select
          style={{ width: 200 }}
          size="large"
          placeholder="Kayıt Kanalı"
          allowClear
          options={[
            { label: "Web", value: "WEB" },
            { label: "Uygulama", value: "APP" },
          ]}
          onChange={handleChannelChange}
        />
        <Select
          style={{ width: 200 }}
          size="large"
          placeholder="Apple Wallet"
          allowClear
          options={[
            { label: "Cüzdanında var", value: true },
            { label: "Cüzdanında yok", value: false },
          ]}
          onChange={handleWalletChange}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          onClick={() => navigate(routes.createClient)}
        >
          Yeni Müşteri
        </Button>
      </div>

      <div style={{ height: 20 }} />
      <Table
        sticky
        rowKey="clientId"
        pagination={false}
        dataSource={tableData}
        columns={ClientColumns()}
        scroll={{ x: "max-content" }}
        loading={tableLoading}
        onChange={handleTableChange}
        onRow={(record) => ({
          onClick: () => goToClientDetails(record.clientId),
          style: { cursor: "pointer" },
        })}
      />
      <div style={{ height: 20 }} />
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Pagination
          onChange={paginationChange}
          total={filter.total}
          showTotal={(total, range) =>
            `${range[0]}-${range[1]} / toplam ${total} kayıt`
          }
          pageSize={filter.size}
          current={filter.page + 1}
          showSizeChanger
          pageSizeOptions={[10, 25, 50, 100, 200]}
        />
      </div>
      <div style={{ height: 20 }} />
      <ModalComponent />
    </>
  );
};

export default Clients;
